import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext.js";
import VideoThumb from "../components/VideoThumb.js";
import SportsLinks from "../components/SportsLinks.js";
import CategoryDropdown from "../components/CategoryDropdown.js";
import "./styles/Home.css";

const categories = [
  "politics",
  "world",
  "business",
  "health",
  "technology",
  "entertainment",
  "sports",
  "local",
];
const categoriesWithSubs = [
  "entertainment",
  "sports",
  "business",
  "technology",
];
const ARTICLES_PER_SECTION = 3;

const Home = () => {
  const { user, token } = useContext(AuthContext);
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState(null);
  const [allSubcategories, setAllSubcategories] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [heroVideoPlaying, setHeroVideoPlaying] = useState(false);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const resp = await fetch("/api/articles", {
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });
        if (!resp.ok) {
          throw new Error(`Failed to load articles: ${resp.status}`);
        }
        const data = await resp.json();
        setArticles(data);
      } catch (err) {
        console.error("Error fetching articles:", err);
        setError(err.message);
      }
    };
    fetchArticles();

    const fetchAllSubcategories = async () => {
      const subcats = {};
      for (const cat of categoriesWithSubs) {
        try {
          const res = await fetch(`/api/subcategories?category=${cat}`);
          const data = await res.json();
          subcats[cat] = data;
        } catch (err) {
          console.error(`Error fetching subcategories for ${cat}:`, err);
          subcats[cat] = [];
        }
      }
      setAllSubcategories(subcats);
    };
    fetchAllSubcategories();
  }, [token]);


  useEffect(() => {
    const es = new EventSource("/api/events");
    es.addEventListener("new-article", () => {
      fetch("/api/articles", {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
        .then((r) => r.json())
        .then((data) => setArticles(data))
        .catch(() => {});
    });
    return () => es.close();
  }, [token]);

  const getExcerpt = (article) => {
    const text = article.excerpt || (article.content ? article.content.substring(0, 120) : "");
    if (text.length > 150) return text.substring(0, 150) + "...";
    return text;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      politics: "Politics",
      entertainment: "Entertainment",
      sports: "Sports",
      local: "Local",
      business: "Business",
      technology: "Technology",
      health: "Health",
      world: "World",
    };
    return labels[type] || type;
  };

  const getArticlesByCategory = (category) => {
    return articles.filter((a) => a.type === category);
  };

  const top = articles[0];

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="header-brand">
          <div className="header-text">
            {user ? (
              <h1 className="login-info">Welcome, {user.username}</h1>
            ) : (
              <h1 className="login-info">
                Please log in to access more features.
              </h1>
            )}
          </div>
        </div>
      </header>

      <nav className="content-bar">
        <div className="content-bar-inner">
          {categories.map((cat) => (
            <div key={cat} className="category-item">
              {categoriesWithSubs.includes(cat) ? (
                <CategoryDropdown id={`category-${cat}`} label={getTypeLabel(cat)}
                  open={openDropdown === cat} onClose={() => setOpenDropdown(null)}
                  onToggle={() =>
                    setOpenDropdown(openDropdown === cat ? null : cat)
                  }
                >
                  {cat === "sports" ? <SportsLinks onClose={() => setOpenDropdown(null)} /> : <>
                  <Link
                    to={`/category/${cat}`}
                    className="dropdown-item"
                    onClick={() => setOpenDropdown(null)}
                  >
                    All {getTypeLabel(cat)}
                  </Link>
                  {(allSubcategories[cat] || []).map((sub) => (
                    <Link
                      key={sub.slug}
                      to={`/category/${cat}/${sub.slug}`}
                      className="dropdown-item"
                      onClick={() => setOpenDropdown(null)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                  </>}
                </CategoryDropdown>
              ) : (
                <Link to={`/category/${cat}`} className="content-bar-link">
                  {getTypeLabel(cat)}
                </Link>
              )}
            </div>
          ))}
        </div>
      </nav>

      {error && (
        <div className="section-container">
          <p className="error">Error loading articles: {error}</p>
        </div>
      )}

      {top && (
        <section className="hero-article">
          <div
            className={`hero-article-inner ${top.image_url ? "has-image" : ""}`}
          >
            <div className="hero-content">
              <span className="hero-kicker">{getTypeLabel(top.type)}</span>
              {top.is_premium && <span className="premium-tag-small" style={{marginLeft: 0, marginBottom: '8px'}}>PREMIUM</span>}
              <h2 className="hero-title">
                <Link to={`/article/${top.id}`}>{top.title}</Link>
              </h2>
              <p className="hero-excerpt">
                {top.excerpt ? (top.excerpt.length > 200 ? top.excerpt.substring(0, 200) + "..." : top.excerpt) : (top.content ? top.content.substring(0, 200) + "..." : "")}
              </p>
              <div className="hero-meta">
                <span className="hero-author">By {top.author_name}</span>
                <span>{formatDate(top.created_at)}</span>
              </div>
            </div>
            {top.image_url && (
              <div className="hero-image-container">
                {top.media_type === 'video' ? (
                  !heroVideoPlaying ? (
                    top.cover_image_url ? (
                      <div className="video-cover-wrapper hero-cover-wrapper" onClick={() => setHeroVideoPlaying(true)}>
                        <img className="hero-image" src={top.cover_image_url} alt={top.title} />
                        <div className="video-cover-overlay">
                          <div className="video-cover-play-btn">&#9654;</div>
                        </div>
                        <span className="video-cover-badge">&#9654; VIDEO</span>
                      </div>
                    ) : (
                      <VideoThumb
                        src={top.image_url}
                        startTime={top.video_start_time}
                        alt={top.title}
                        imgClassName="hero-image"
                        wrapperClassName="hero-cover-wrapper"
                        onClick={() => setHeroVideoPlaying(true)}
                        showBadge
                      />
                    )
                  ) : (
                    <video
                      className="hero-image"
                      controls
                      playsInline
                      preload="metadata"
                      autoPlay
                    >
                      <source
                        src={top.image_url}
                        type={top.image_url.endsWith('.webm') ? 'video/webm' : top.image_url.endsWith('.mov') ? 'video/quicktime' : 'video/mp4'}
                      />
                    </video>
                  )
                ) : (
                  <img className="hero-image" src={top.image_url} alt={top.title} />
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {categories.map((category) => {
        const categoryArticles = getArticlesByCategory(category);
        if (categoryArticles.length === 0) return null;

        const displayedArticles = categoryArticles.slice(
          0,
          ARTICLES_PER_SECTION,
        );
        const hasMore = categoryArticles.length > ARTICLES_PER_SECTION;

        return (
          <section key={category} className="category-section">
            <div className="section-heading">
              <h2>{getTypeLabel(category)}</h2>
              {hasMore && (
                <Link to={`/category/${category}`} className="see-more-link">
                  See More
                </Link>
              )}
            </div>

            <div className="articles-row">
              {displayedArticles.map((a) => (
                <Link key={a.id} to={`/article/${a.id}`} className="article-card article-card-link">
                  <div className="article-card-content">
                    {a.image_url && (
                      a.media_type === 'video' ? (
                        a.cover_image_url ? (
                          <div className="card-video-thumb article-image card-has-cover">
                            <img src={a.cover_image_url} alt={a.title} className="card-cover-img" />
                            <span className="video-badge card-video-badge">VIDEO</span>
                          </div>
                        ) : (
                          <VideoThumb
                            src={a.image_url}
                            startTime={a.video_start_time}
                            alt={a.title}
                            imgClassName="card-cover-img"
                            wrapperClassName="card-video-thumb article-image card-has-cover"
                            showOverlay={false}
                          />
                        )
                      ) : (
                        <img className="article-image" src={a.image_url} alt={a.title} />
                      )
                    )}
                    <h3 className="home-article-title">
                      {a.title}
                      {a.is_premium && <span className="premium-tag-small">PREMIUM</span>}
                    </h3>
                    <p className="article-excerpt">{getExcerpt(a)}</p>
                    <div className="article-meta">
                      <span>By {a.author_name}</span>
                      <span>|</span>
                      <span>{formatDate(a.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      {articles.length === 0 && !error && (
        <div className="section-container">
          <p className="no-articles">No articles found.</p>
        </div>
      )}
    </div>
  );
};

export default Home;
